import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { type Pagination } from "../../services/stores/types";
import { diagramAPI } from "./diagramAPI";
import {
  type DiagramSimple,
  type DiagramFull,
  type DiagramWithQuestionsFull,
  type CreateDiagramInput,
  type UpdateDiagramInput,
} from "./types/types";

/**
 * Hook customizado para gerenciar operações relacionadas a diagrams.
 *
 * @param token - Token de autenticação para requisições de API.
 */
export const useDiagrams = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "diagrams",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      diagramAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const diagrams: DiagramSimple[] = data?.diagrams || [];
  const fetchedPagination: Pagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createDiagramMutation = useMutation({
    mutationFn: (diagramData: CreateDiagramInput) =>
      diagramAPI.create(diagramData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diagrams"] });
    },
  });

  const updateDiagramMutation = useMutation({
    mutationFn: (diagramData: UpdateDiagramInput) =>
      diagramAPI.update(diagramData.id, diagramData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diagrams"] });
    },
  });

  const deleteDiagramMutation = useMutation({
    mutationFn: (id: string) => diagramAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diagrams"] });
    },
  });

  const getDiagramById = useCallback(
    async (id: string): Promise<DiagramFull | null> => {
      try {
        const diagramDetails = await diagramAPI.getById(id, token);
        return diagramDetails;
      } catch (err) {
        console.error("Error fetching diagram by ID:", err);
        return null;
      }
    },
    [token]
  );


  const getDiagramByIdFull = useCallback(
    async (id: string): Promise<DiagramWithQuestionsFull | null> => {
      try {
        const diagramDetails = await diagramAPI.getByIdFull(id, token);
        return diagramDetails;
      } catch (err) {
        console.error("Error fetching diagram with questions by ID:", err);
        return null;
      }
    },
    [token]
  );


  return {
    diagrams,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createDiagram: createDiagramMutation.mutateAsync,
    updateDiagram: updateDiagramMutation.mutateAsync,
    deleteDiagram: deleteDiagramMutation.mutate,
    getDiagramById,
    getDiagramByIdFull,
    changePage,
    isCreating: createDiagramMutation.isPending,
    isUpdating: updateDiagramMutation.isPending,
    isDeleting: deleteDiagramMutation.isPending,
  };
};
